import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, BellOff, CheckCheck, Inbox, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { pushSupported, getPushSubscription, enablePush, disablePush } from "../lib/push";

const ago = (iso) => {
  const m = Math.floor((Date.now() - new Date(iso)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
};

export default function NotificationBell({ idSuffix = "" }) {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const ref = useRef();
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    getPushSubscription().then((s) => setPushOn(!!s)).catch(() => {});
    const t = setInterval(load, 30000);
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { clearInterval(t); document.removeEventListener("mousedown", close); };
  }, []);

  const openItem = async (n) => {
    if (!n.read) await api.put(`/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };

  const readAll = async () => { await api.put("/notifications/read-all").catch(() => {}); load(); };

  const togglePush = async () => {
    try {
      if (pushOn) { await disablePush(); setPushOn(false); toast.success("Phone alerts turned off"); }
      else { await enablePush(); setPushOn(true); toast.success("Phone alerts enabled on this device"); }
    } catch (err) { toast.error(err?.message || apiError(err)); }
  };

  const standaloneHint = /iphone|ipad/i.test(navigator.userAgent) && !window.navigator.standalone;

  return (
    <div ref={ref} className="relative">
      <button data-testid={`notif-bell-btn${idSuffix}`} onClick={() => setOpen(!open)} aria-label="Notifications"
        className="relative w-9 h-9 rounded-xl border-2 border-[#171B33] bg-white flex items-center justify-center hover:bg-lime-200 transition-colors">
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span data-testid={`notif-unread-count${idSuffix}`} className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full border-2 border-[#171B33] bg-[#FCD34D] text-[10px] font-extrabold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid={`notif-dropdown${idSuffix}`} className="absolute right-0 mt-2 w-80 sm:w-96 nb bg-white z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#171B33]">
            <p className="font-display font-extrabold">Notifications</p>
            {data.unread > 0 && <button data-testid="notif-read-all-btn" onClick={readAll} className="text-xs font-bold flex items-center gap-1 hover:text-lime-600"><CheckCheck className="w-3.5 h-3.5" /> Mark all read</button>}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {data.items.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">You're all caught up.</p>}
            {data.items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)}
                className="w-full text-left px-4 py-3 flex gap-3 border-b border-slate-100 hover:bg-slate-50 transition-colors"
                style={{ background: n.read ? undefined : "#F7FEE7" }}>
                <span className="w-8 h-8 rounded-lg border-2 border-[#171B33] flex items-center justify-center shrink-0" style={{ background: n.kind === "inquiry" ? "#FDBA74" : "#93C5FD" }}>
                  {n.kind === "inquiry" ? <Inbox className="w-4 h-4" /> : <Megaphone className="w-4 h-4" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-bold truncate">{n.title}</span>
                  {n.body && <span className="block text-xs text-slate-600 line-clamp-2">{n.body}</span>}
                  <span className="block text-[11px] text-slate-400 mt-0.5">{ago(n.created_at)}</span>
                </span>
              </button>
            ))}
          </div>
          {pushSupported() && (
            <div className="px-4 py-3 border-t-2 border-[#171B33] flex items-center justify-between gap-3" style={{ background: "#F8FAFC" }}>
              <div className="min-w-0">
                <p className="text-xs font-bold flex items-center gap-1.5">{pushOn ? <BellRing className="w-3.5 h-3.5 text-lime-600" /> : <BellOff className="w-3.5 h-3.5 text-slate-400" />} Phone alerts {pushOn ? "on" : "off"}</p>
                <p className="text-[11px] text-slate-500">{standaloneHint ? "On iPhone, first add the app to your Home Screen." : "Get alerts on your lock screen even when the app is closed."}</p>
              </div>
              <button data-testid={`push-toggle-btn${idSuffix}`} onClick={togglePush} className={`nb-btn nb-btn-sm shrink-0 ${pushOn ? "" : "nb-btn-lime"}`}>{pushOn ? "Turn off" : "Enable"}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
